// The ASCII spellings a language accepts where its own alphabet asks for a letter no English
// keyboard carries — German's ä→ae, ö→oe, ü→ue, ß→ss. The app has accepted them for as long as it
// has had an answer field (Util.umlautSubst whole-string in Practice, character by character in
// Learn's typing rungs, the TRANSLIT index in the dictionary). What was missing was anywhere that
// SAYS so: a learner on a keyboard without umlauts had to guess, and the natural guess — the bare
// base letter, "a" for "ä" — is the one substitution the strict rule rejects.
//
// ONE table, read by every place a word is typed: the drill's answer field and the dictionary
// lookup field (inset at the field's right edge, inside the field), and Learn's typing rungs (a
// line under the field, whose fields are a letter or two wide with no room inside them).
//
// Two things this deliberately does NOT do:
//
//   • It never narrows to the word on screen. Showing "ü→ue" only when the answer HAS a ü would
//     announce that the answer has a ü. The mapping is a property of the LANGUAGE, so it stands
//     for the whole session or not at all.
//   • It never promises more than the surface delivers. `not` marks a pair that holds in some
//     places and not others. No pair carries one today: ß→ss was flagged off the dictionary lookup
//     until 2026-08-12, because the prefix index routed a query containing "ss" to the plain index
//     and "gross" therefore did not find "groß" — the owner asked for the pair, so the ROUTING was
//     fixed instead (GermanSearchSupport.analyzeQuery) and the claim is now true everywhere. The
//     flag stays because the next language to want a row may not be so lucky.
//
// A language with no row shows nothing at all — the right answer for English and Russian, where
// nothing needs substituting, and the reason adding a language here is adding a row and nothing
// else. Same shape and intent as AnswerForms.RULES, which does the mirror job for the direction
// where the MEANING is typed.
var TypeSubst = (function () {
	"use strict";

	var ANSWER = "answer";   // a word typed to be graded — Practice's field, Learn's typing rungs
	var LOOKUP = "lookup";   // a word typed to be found — the dictionary lookup field

	// Spanish is the language the `not` flag was reserved for, and it arrives with the flag SET
	// (F2b, 2026-09-08 — the coupled ruling register item 11 held open: the display half is decided
	// together with drill strictness, never before it).
	//
	// The two halves of the ruling point opposite ways, which is the whole reason the pairs are
	// surface-scoped rather than absent:
	//
	//   LOOKUP  the dictionary index FOLDS. Language.ES carries `diacriticsArr()`, so every term
	//           gets a FOLD key beside its ORIG_LOWER one and DefaultSearchSupport.analyzeQuery
	//           routes every query to FOLD (DiacriticCharsReplacer.FOLD: á→a é→e í→i ó→o ú→u,
	//           and ñ→n). Typing `nino` really does find `niño` and `hablo` really does find
	//           `habló`, so the claim is true and worth making. (`ü` folds to `u` by the same
	//           rule; it is left off the row because it occurs only in `güe`/`güi` and a seventh
	//           chip costs the field more room than that is worth.)
	//
	//   ANSWER  the GRADER never folds — register rule P6, and `packs/es.py:GRADER_FOLDS` is an
	//           empty tuple whose emptiness IS the policy. A missing accent is a near-miss tier
	//           that shows the correction, and a STRICTER one that names the other word when the
	//           unaccented string is itself valid (`hablo`/`habló`, `papa`/`papá`). So a chip
	//           saying `á→a` beside the answer field would promise credit the grader refuses,
	//           which is exactly what the header above forbids.
	//
	// ⚠️ German has no `not` because both its surfaces really do accept `ae`. Spanish's asymmetry
	// is not a Spanish quirk either — it is what a language whose diacritics are MEANING-BEARING
	// looks like, and the next such language should copy this row rather than German's.
	var RULES = {
		DE: [
			{ from: "ä", to: "ae" },
			{ from: "ö", to: "oe" },
			{ from: "ü", to: "ue" },
			{ from: "ß", to: "ss" }
		],
		ES: [
			{ from: "á", to: "a", not: ANSWER },
			{ from: "é", to: "e", not: ANSWER },
			{ from: "í", to: "i", not: ANSWER },
			{ from: "ó", to: "o", not: ANSWER },
			{ from: "ú", to: "u", not: ANSWER },
			{ from: "ñ", to: "n", not: ANSWER }
		]
	};

	var GAP = 12;   // px of clear air the typed text keeps before it is judged to have reached the mapping

	// The least typing room a field may keep and still be worth calling a field. Below this the
	// mapping is not an aid, it is a squeeze: the reserve it asks for no longer fits in the row, the
	// field's own frame is pushed out past the card that holds it, and the reader is left with a
	// couple of characters' worth of field (owner report 2026-08-12 — the answer field's orange
	// frame running out of the answer card on a narrow pane, with the reveal button also at that
	// edge). So the mapping steps aside for the same reason it steps aside for typed text: the word
	// comes first. ~1.5 characters at the answer field's own 2.4rem.
	var MIN_ROOM = 48;

	// The pairs this language offers on this surface, in display order.
	function pairs(lang, surface) {
		var row = RULES[String(lang || "").toUpperCase()] || [];
		var out = [];
		for (var i = 0; i < row.length; i++) {
			if (row[i].not !== surface) { out.push(row[i]); }
		}
		return out;
	}

	// One "ä→ae" chip per pair into `host`, or an empty host that takes itself off screen when the
	// language has none. Hidden by a CLASS rather than by jQuery .hide()/.show(), which would
	// clobber the flex row on the way back (docs/feedback/jquery-show-breaks-flex.md), and rather
	// than by an inline display, which would then out-specify the "the text has reached it" rule.
	function fill(host, lang, surface) {
		var ps = pairs(lang, surface);
		host.empty();
		for (var i = 0; i < ps.length; i++) {
			host.append($("<span>").addClass("typeSubstPairClass")
				.text(ps[i].from + "→" + ps[i].to));
		}
		host.toggleClass("typeSubstOffClass", !ps.length);
		return ps.length > 0;
	}

	// How wide the mapping renders — measured on a copy parked off-screen on <body>, NOT on the
	// mapping itself. A field is routinely painted while its tab is hidden (Practice deals its
	// first card before the box is shown, the dictionary is repainted on a pair change from
	// another tab), and an element inside a hidden panel measures zero — which would reserve
	// nothing and let typed text run under the mapping the moment the panel appeared. The width is
	// a pure function of the pairs and the font, both of which the copy carries in its classes, so
	// it can be measured anywhere and is right the first time.
	// The copy drops its id and every edge offset first. Keeping the id would bring the site's own
	// positioning rule with it ("#answerSubstId { right: 2% }"), and an absolutely positioned box
	// pinned by BOTH left and right is stretched to fit between them rather than to its content —
	// which measured the answer field's mapping at eleven thousand pixels and padded the field out
	// to match. Classes carry the type; the id carries the placement, and placement is exactly what
	// a measurement must not inherit.
	function measure(host) {
		var probe = host.clone().removeAttr("id").css({
			position: "absolute", left: "-9999px", top: "0", right: "auto", bottom: "auto",
			width: "auto", display: "flex", visibility: "hidden"
		}).appendTo(document.body);
		var w = Math.ceil(probe.outerWidth());
		probe.remove();
		return w;
	}

	// Paint an IN-FIELD mapping and reserve the room it takes, so typed text never runs underneath
	// it. `box` is the positioning parent (#answerBoxId / #lookupBoxId): it carries the marker class
	// the layout keys off, and --pl-type-subst-w, the mapping's measured width. Measured rather
	// than guessed, so a longer row, a second language or a bigger font still fits — and so the
	// other things pinned to that same edge (the drill's reveal button, the dictionary's direction
	// pill) can be written against the same one number in CSS.
	function apply(box, host, field, lang, surface) {
		var shown = fill(host, lang, surface);
		box.toggleClass("hasTypeSubstClass", shown);
		// A fresh mapping is on screen and the field is empty again, so the "text has reached it"
		// state lifts and the room it left has to be measured anew — the field may have changed
		// width, or the reveal button may have come and gone since.
		box.removeClass("typeSubstHiddenClass").data("typeSubstRoom", null);
		box[0].style.setProperty("--pl-type-subst-w", (shown ? measure(host) : 0) + "px");
		remember(box, host, field, lang, surface);
		observe(box);
		// …and ask, before the reader ever sees it, whether the row it just claimed is a row this
		// field can spare (MIN_ROOM). A mapping painted first and judged only on the first keystroke
		// would show its overflow for exactly as long as it took to type one.
		if (shown) { fit(field, box, host); }
		return shown;
	}

	// ── standing aside for what is being typed ──────────────────────────────────
	//
	// The mapping is help for a word not yet typed; the moment the word itself needs the room, the
	// mapping is in the way. So it steps aside as the typed text arrives — and gives the padding
	// back with it, which is the point: an aid that reserved space it no longer uses would just be
	// a narrower field.
	//
	// The width of the typed text is measured on a canvas in the field's own font rather than read
	// off the DOM: an <input> reports no geometry for its value, and scrollWidth only tells you the
	// text has ALREADY overflowed, which is one keystroke too late.
	var measureCtx = null;

	function textWidth(field) {
		var value = field.val();
		if (!value) { return 0; }
		if (!measureCtx) { measureCtx = document.createElement("canvas").getContext("2d"); }
		var cs = window.getComputedStyle(field[0]);
		measureCtx.font = cs.fontStyle + " " + cs.fontWeight + " " + cs.fontSize + " " + cs.fontFamily;
		return measureCtx.measureText(value).width;
	}

	// How much text the field can hold before it reaches the mapping — measured ONCE per painting,
	// while the mapping is still on screen, and cached on the box.
	//
	// Measuring it only in that state is what keeps this from oscillating. Hiding the mapping gives
	// the field its padding back, so a room re-measured afterwards would be wider, the text would
	// "fit" again, the mapping would come back, and the same keystroke would hide it again. The
	// question asked is always the same one — does the text fit ALONGSIDE the mapping — so the
	// answer only ever flips when the text itself changes.
	function room(field, box, host) {
		var cached = box.data("typeSubstRoom");
		if (cached != null) { return cached; }
		if (!box.hasClass("hasTypeSubstClass") || box.hasClass("typeSubstHiddenClass")) { return null; }
		var hr = host[0].getBoundingClientRect();
		var fr = field[0].getBoundingClientRect();
		if (!hr.width || !fr.width) { return null; }   // painted while the tab was hidden — try again later
		var cs = window.getComputedStyle(field[0]);
		var textLeft = fr.left + (parseFloat(cs.borderLeftWidth) || 0) + (parseFloat(cs.paddingLeft) || 0);
		var px = hr.left - textLeft - GAP;
		box.data("typeSubstRoom", px);
		return px;
	}

	// The one verdict, and the only writer of the stand-aside class: the mapping steps back when the
	// field cannot spare the room at all (MIN_ROOM) and when what is being typed has reached it.
	// Asked from three sides — the painting (apply), every keystroke (watch), and whatever else moves
	// on that edge (the reveal button appearing, the row changing width).
	function fit(field, box, host) {
		if (!field || !field.length || !box.hasClass("hasTypeSubstClass")) { return; }
		var px = room(field, box, host);
		if (px == null) { return; }
		box.toggleClass("typeSubstHiddenClass", px < MIN_ROOM || textWidth(field) > px);
	}

	// Bind a field to its mapping. Returns the update function, so a caller that changes the
	// geometry itself (the drill's reveal button appearing beside the mapping) can re-ask after
	// invalidating the cached room. Namespaced + off-first: a new drill session re-binds the same
	// field, and two handlers would do the same work twice.
	function watch(field, box, host) {
		function update() { fit(field, box, host); }
		field.off(".typeSubst").on("input.typeSubst keyup.typeSubst", update);
		return update;
	}

	// Text measured before the web font swaps in comes out a few pixels short of the final
	// rendering, and the dictionary's mapping is painted during boot — early enough to catch the
	// fallback font. Every mapping applied so far is redone once the fonts settle: a one-off, and
	// the only moment the width can change without a caller asking for it. One entry per box, so
	// re-applying does not grow the list.
	var applied = [];

	function remember(box, host, field, lang, surface) {
		for (var i = 0; i < applied.length; i++) {
			if (applied[i][0][0] === box[0]) { applied[i] = [box, host, field, lang, surface]; return; }
		}
		applied.push([box, host, field, lang, surface]);
	}

	function reapply(box) {
		for (var i = 0; i < applied.length; i++) {
			var a = applied[i];
			if (box && a[0][0] !== box) { continue; }
			apply(a[0], a[1], a[2], a[3], a[4]);
		}
	}

	if (document.fonts && document.fonts.ready) {
		document.fonts.ready.then(function () { reapply(null); });
	}

	// The other moment a mapping's width stops being the right width: its ROW changed. Both reserves
	// are measured against the row the field sits in, and that row moves without any card changing —
	// a side pane folds, the window is resized — which is exactly when the fit question (MIN_ROOM)
	// flips. Observed rather than bound to `resize`, because folding a pane is not a window resize.
	// The last width applied at is kept per box and an unchanged one is dropped on the floor: apply()
	// writes padding on the FIELD, and the observer must never be able to answer its own writes.
	var sizes = window.WeakMap ? new WeakMap() : null;
	var boxObserver = (window.ResizeObserver && sizes) ? new ResizeObserver(function (entries) {
		for (var i = 0; i < entries.length; i++) {
			var el = entries[i].target;
			var w = Math.round(el.getBoundingClientRect().width);
			if (!w || sizes.get(el) === w) { continue; }
			sizes.set(el, w);
			reapply(el);
		}
	}) : null;

	function observe(box) {
		if (!boxObserver || sizes.has(box[0])) { return; }
		sizes.set(box[0], Math.round(box[0].getBoundingClientRect().width));
		boxObserver.observe(box[0]);
	}

	// The cached room is void whenever something else on that edge moves.
	function remeasure(box) { box.data("typeSubstRoom", null); }

	return { ANSWER: ANSWER, LOOKUP: LOOKUP, pairs: pairs, fill: fill, apply: apply,
		watch: watch, fit: fit, remeasure: remeasure };
})();
